/** W82: Kontext der Geist-Ebene (GeistEbene.tsx) mit `useGeist()` für Seiten und Bausteine. */
import { createContext, useContext } from "react";
import type { SatzTeil } from "./blasenForm";
import type { Anlass } from "./darf";

export type MarkierFarbe = "ueberfaellig" | "erledigt" | "funke" | "licht";

export type GeistApi = {
  /** Fliegt zum Ziel (Ecke des Elements), wenn `darf(anlass)`; sonst nur Sprung mit Markierung. Unterwegs trägt er den
   *  Satz in der Blase unterwegs. Ein neuer Flug startet am aktuellen Punkt. */
  fliege: (ziel: Element, anlass: Anlass, satz?: SatzTeil[], opt?: { erklaert?: boolean; schritt?: string; weiter?: () => void; fokus?: boolean; bezug?: Element | null }) => Promise<boolean>;
  /** Zurück auf seinen Platz neben der Kopfblase („Danke“, Escape, Platz wieder sichtbar). */
  heim: () => void;
  /** Markiert eine angesprungene Stelle (Rahmen in der Farbe des Anlasses). */
  markiere: (el: Element, farbe?: MarkierFarbe) => void;
  /** Ein Satz zu einer Aktion nach der Sicht-Regel (D-420): Figur im Bild → false (die Seite zeigt den Satz an seinem
   *  Platz); außer Sicht kommt er ins Bild (Überall im Flug, Erklären umgeblendet und nur mit `erklaert`), spricht 1,8 bis
   *  3 s und kehrt zurück; ruhig auf der Startseite (`rand`) als Randgeist; „Keine“ stumm. `ziel`: das bediente Element. */
  sage: (satz: SatzTeil[], opt?: { erklaert?: boolean; ziel?: Element | null; rand?: boolean }) => boolean;
  unterwegs: boolean;
  /** W82 Web-5h3 (HW6, HW9): `tun` läuft, sobald keine Antwort des Gaist mehr steht (Blase unterwegs, Randgeist, Satz am
   *  Platz nach `halten`); steht keine, sofort. Nie zwei Meldungen zugleich: das Lern-Banner kommt erst nach „Danke“. */
  nachAntwort: (tun: () => void) => void;
  /** Die Seite zeigt eine Antwort an seinem Platz (`ms` lang); `nachAntwort` wartet so lange. */
  halten: (ms: number) => void;
  /** Die Geist-Szene meldet ihren Platz (Ausgang und Ziel der Heimkehr). */
  platzMelden: (el: HTMLElement | null) => void;
  /** Akten-Auftritt (Vorlage `aktenAuftritt`): auf der Karte erscheinen, zur nächsten Karte fliegen (true am Ziel, false
   *  abgebrochen), dort hüpfen, die Figur an die Karte übergeben (die Ebene blendet ihn aus). */
  auftritt: { zeige: (karte: Element) => void; fliege: (karte: Element) => Promise<boolean>; hopse: () => void; uebergeben: () => void };
};

export const GeistKontext = createContext<GeistApi | null>(null);

export function useGeist(): GeistApi {
  const api = useContext(GeistKontext);
  if (!api) throw new Error("useGeist() braucht eine GeistEbene");
  return api;
}

/** Für Bausteine, die auch ohne Ebene stehen (Geist-Szene). */
export function useGeistOptional(): GeistApi | null {
  return useContext(GeistKontext);
}

